import { memo, FC, Dispatch, SetStateAction } from 'react'
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertDescription,
  Button,
  Card,
  Checkbox,
  CheckboxGroup,
  Flex,
  HStack,
  Stack,
  Text,
} from '@chakra-ui/react'

import { ImpactModule } from 'types'
import { CheckCircle } from 'components/icons'

type Props = {
  isDisabled?: boolean
  MODULE: ImpactModule
  availablePermissions: any[]
  selectedPermissions: any[]
  setSelectedPermissions: Dispatch<SetStateAction<any[]>>
}

const PermissionsForm: FC<Props> = ({
  isDisabled = false,
  MODULE,
  availablePermissions,
  selectedPermissions,
  setSelectedPermissions,
}) => {
  function handleCheckAllPermissions(permissions: any[]) {
    const permissionKeys: string[] = permissions.map(({ key }) => key)
    const newPermissions: string[] = [...new Set([...selectedPermissions, ...permissionKeys])]
    setSelectedPermissions(newPermissions)
  }

  function handleUncheckAllPermissions(permissions: any[]) {
    const permissionKeys: string[] = permissions.map(({ key }) => key)
    const newPermissions: string[] = selectedPermissions.filter((key) => !permissionKeys.includes(key))
    setSelectedPermissions(newPermissions)
  }

  function openAccordionIfAtLeastOnePermissionIsChecked(permissions: any[]): number {
    const permissionsKeys: string[] = permissions.map(({ key }) => key)
    const hasAtLeastOnePermissionChecked = permissionsKeys.some((key) => selectedPermissions.includes(key))
    return hasAtLeastOnePermissionChecked ? 0 : 1
  }

  function handleOnChange(keys: string[]) {
    setSelectedPermissions(keys)
  }

  return (
    <CheckboxGroup size="md" value={selectedPermissions} onChange={handleOnChange}>
      {availablePermissions.map(({ page, permission }) => {
        const shouldOpenAccordion = openAccordionIfAtLeastOnePermissionIsChecked(permission)
        if (!MODULE.PAGES) return
        const pageInformation = MODULE.PAGES[page.toUpperCase()]

        return (
          <Accordion
            key={page}
            // defaultIndex={[shouldOpenAccordion]}
            allowMultiple
            allowToggle
          >
            <AccordionItem mb={2} border="none">
              <Card bg="light" p={0}>
                <AccordionButton p={2} className="accessible" _hover={{ bg: 'lightPrimary' }}>
                  <Flex align="center" width="100%" justify="space-between">
                    <Stack align="flex-start" spacing={1}>
                      <Text>{pageInformation?.name || page}</Text>
                      <Text variant="secondary">{pageInformation?.description}</Text>
                    </Stack>
                    <Flex align="center">
                      {shouldOpenAccordion === 0 && <CheckCircle color="primary" />}
                      <AccordionIcon />
                    </Flex>
                  </Flex>
                </AccordionButton>

                <AccordionPanel borderTopWidth="1px" borderColor="borderColor" p={2}>
                  <Flex align="center" justify="space-between" p={2}>
                    <Button isDisabled={isDisabled} size="xs" onClick={() => handleCheckAllPermissions(permission)}>
                      Marcar todas
                    </Button>

                    <Button
                      isDisabled={isDisabled}
                      size="xs"
                      colorScheme="red"
                      variant="ghost"
                      onClick={() => handleUncheckAllPermissions(permission)}
                    >
                      Desmarcar todas
                    </Button>
                  </Flex>
                  {permission.map(({ name, key, alert }: { name: string; key: string; alert: any }) => (
                    <HStack p={2} key={key} alignItems="start">
                      <Checkbox isDisabled={isDisabled} value={key} />
                      <Flex flexDir="column" gap={2}>
                        <Text>{name}</Text>
                        {alert && (
                          <Alert status={alert.type.toLowerCase()} variant="top-accent" px="4px" py="2px">
                            <AlertDescription fontSize="xs">{alert.message}</AlertDescription>
                          </Alert>
                        )}
                      </Flex>
                    </HStack>
                  ))}
                </AccordionPanel>
              </Card>
            </AccordionItem>
          </Accordion>
        )
      })}
    </CheckboxGroup>
  )
}

export default memo(PermissionsForm)
